/**
 * This file is an alternative of `product-model.js` on Shopify.
 * It overloads the default model-viewer with the `webgi-viewer` from the iJewel3D SDK(https://ijewel3d.com).
 * This file must be included in the theme manually, and referenced in the pages where product-model.js was used.
 * Check the docs for more information and updates. - https://developer.ijewel3d.com/integrations/shopify
 *
 * File version - 26-07-2025
 */

// Settings for the iJewel3D SDK, this includes the webgi package version, environment maps etc.
// This can be set externally in the metafields or in the page where the product model is used, if not, the default settings will be used.
window.iJewelSDKSettings = {
    webgi_version: "0.15.0",
    scene_settings: "",
    environment_map: "",
    loading: {
        loadingTextHeader: "Loading your Jewelry",
        showFileNames: false,
        backgroundOpacity: 0.5,
        background: "#ffffff",
        textColor: "#222222",
        logoImage: "https://cdn.shopify.com/s/files/1/0681/0042/3782/files/Logo-Small_1.png?v=1769422490",
    },
    ...(window.iJewelSDKSettings||{}),
}

// Create a custom model-viewer element that extends the HTMLElement, this will be used to load the iJewel3D SDK viewer.
// Note that it has to be made sure that this should be run before loading the google model viewer, since we cannot redefine an html element.
if(!customElements.get('model-viewer')){
    const viewerScript = document.createElement('script');
    viewerScript.setAttribute('src',
        window.iJewelSDKSettings.webgi_version.includes('/') ? window.iJewelSDKSettings.webgi_version : // assume URL
            'https://releases.ijewel3d.com/webgi/runtime/viewer-'+(window.iJewelSDKSettings.webgi_version||'0.9.19')+'.js'
    );
    document.head.appendChild(viewerScript);
    //todo: fix for proper multiple viewer support.
    let globalViewer = null; // to make sure we use only 1 viewer and canvas on a page,
    customElements.define('model-viewer', class ModelViewerWebGi extends HTMLElement{
        constructor() {
            super();
            const container = document.createElement('div')
            this.container = container
            this.container.style.width = "100%"
            this.container.style.height = "100%"
            this.appendChild(container)

            this.modelLoaded = false;

            this.initialize()
        }

        doOnLoad(fn){
            if(this.modelLoaded) fn()
            this.onLoad = fn;
        }

        async initialize(){
            if(globalViewer) {
                if(globalViewer === 1) return
                this.viewer = globalViewer
                this.viewer.scene.removeSceneModels()
            }else {
                globalViewer = 1;
                this.viewer = new CoreViewerApp({container: this.container})
                await this.viewer.initialize({caching: true, ground: true, bloom: true, enableDrop: false, importPopup: true, debug: false})

                // ENCRYPTED_MODELS
                // Uncomment this and set your password to load encrypted models.
                // viewer.getManager().importer.registerFile("temp.glb").preparsers[0].key = (encryption, json)=> {
                //   // console.log(encryption, json);
                //   return "password";
                // };

                globalViewer = this.viewer;
                this.viewer.canvas.style.width = "100%"
                this.viewer.canvas.style.height = "100%"
            }
            const src = this.getAttribute('src')
            let envMapPromise = null
            this.refreshLoadingScreen()
            if(window.iJewelSDKSettings.environment_map && window.iJewelSDKSettings.environment_map.length){
                envMapPromise = this.viewer.getManager().importer.importSinglePath(window.iJewelSDKSettings.environment_map)
            }
            const modelPromise = this.viewer.load(src)
            const [model, envMap] = await Promise.all([modelPromise, envMapPromise])
            this.refreshLoadingScreen()
            if(window.iJewelSDKSettings.scene_settings && window.iJewelSDKSettings.scene_settings.length){
                await this.viewer.load(window.iJewelSDKSettings.scene_settings)
            }
            this.view = this.viewer.plugins.CameraViews.getCurrentCameraView()
            this.refreshLoadingScreen()
            if(envMap) this.viewer.scene.setEnvironment(envMap)
            this.modelLoaded = true

            // await timeout(200)
            setTimeout(()=>{
                if(this.onLoad) this.onLoad()
                window.dispatchEvent(new CustomEvent('ijewel-viewer-loaded', {detail: this}))
            }, 200)

        }

        refreshLoadingScreen(){
            const loadingSettings = window.iJewelSDKSettings.loading ?? {}
            const loadingPlugin = this.viewer.getPluginByType('LoadingScreenPlugin')
            Object.entries(loadingSettings).forEach(([key, value])=>{
                loadingPlugin[key] = value
            })
        }

        resetView(){
            if(!this.view || !this.viewer) return
            this.viewer.plugins.CameraViews.animateToView(this.view, 500)
        }
        
        pause(){}

    })
}


// Create a custom ProductModel element that extends the DeferredMedia element, this disables loading of google-model-viewer
if (!customElements.get('product-model')) {
    customElements.define('product-model', class ProductModel extends DeferredMedia {
        constructor() {
            super();
            const template = this.querySelector('template')
            const viewer = template.content.querySelector('model-viewer')
            delete viewer.dataset.shopifyFeature // disables loading of google model viewer script by shopify

            const closeButton = document.createElement('div')
            closeButton.className = 'deferred-media__close-button'
            closeButton.innerHTML = '&times;'
            Object.assign(closeButton.style, {
                padding: '5px', position: 'absolute',
                top: '0', right: '0', margin: '10px', zIndex: 101,
                cursor: 'pointer',
                borderRadius: '50%', lineHeight: '20px', fontSize: '16px',
                display: 'flex', justifyContent: 'center', alignItems: 'center',
                backgroundColor: 'rgb(var(--color-background))',
                border: '.1rem solid rgba(var(--color-foreground), .1)',
                color: 'rgb(var(--color-foreground))',
                height: '28px',
                width: '28px',
                transform: 'scale(1)',
                transition: 'transform var(--duration-short) ease, color var(--duration-short) ease',
            });
            closeButton.addEventListener('click', async () => {
                if(this.modelViewer){
                    await this.modelViewer.resetView()
                }
                const poster = this.querySelector('.deferred-media__poster')
                const posterButton = this.querySelector('.deferred-media__poster-button')
                // const poster = document.querySelectorAll('.deferred-media__poster')[0]
                // const posterButton = document.querySelectorAll('.deferred-media__poster-button')[0]
                posterButton.style.display = 'flex'
                poster.style.display = 'flex'
                closeButton.style.display = 'none'
            })
            this.closeButton = closeButton;
        }

        loadContent() {
            super.loadContent();

            this.closeButton.style.display = 'flex'
            const poster = this.querySelector('.deferred-media__poster')
            poster.style.display = 'block'
            poster.style.zIndex = 100;
            const posterButton = this.querySelector('.deferred-media__poster-button')
            posterButton.style.display = 'none'

            const modelViewer = this.querySelector('model-viewer')
            modelViewer.doOnLoad(()=>{
                modelViewer.viewer.container.appendChild(this.closeButton)
                poster.style.display = 'none'
            })
        }

        setupModelViewerUI(errors) {
            if (errors) return;
        }
    });
}

// Set a dummy ProductModel in the window, just in case
window.ProductModel = {
    loadShopifyXR() {
    },

    setupShopifyXR(errors) {
        if (errors) return;
    },
};